import React, { useEffect, useState } from 'react';
import { Container, Alert, TextField, Button, Typography, Grid, MenuItem, Autocomplete, Box } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import walletApi from '../../api/wallet';
import CurrencyInput from '../commons/CurrencyInput';
import PositionSelector from './PositionSelector';
import { getCurrencyTypes } from '../../utils/Currency';
import { getErrorMessage } from '../../utils/errors';

const CashInForm = ({ onCreate }) => {
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currency, setCurrency] = useState('BRL');
  const [amount, setAmount] = useState(0);
  const [receivedDate, setReceivedDate] = useState(dayjs().locale('pt-br').format('DD/MM/YYYY'));
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const Currencytypes = getCurrencyTypes();

  const showPositionSelector = selectedCategory?.AllowsPositionLink === true;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await walletApi.get('/category?category_type=cash_in')
        setCategories(response.data.Categories);
      } catch (err) {
        setError(getErrorMessage(err, 'Falha ao carregar categorias.'));
      }
    };

    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        Description: description,
        CategoryId: selectedCategory ? selectedCategory.ID : null,
        Currency: currency,
        Amount: String(amount),
        ReceivedDate: receivedDate,
      };
      if (selectedPosition) {
        payload.position_id = selectedPosition.id;
        payload.ticker = selectedPosition.assetName;
      }
      await walletApi.post('/cashin', payload);
      setSuccess('Lançamento criado com sucesso!');
      setDescription('');
      setSelectedCategory(null);
      setCurrency('BRL');
      setAmount(0);
      setSelectedPosition(null);
      if (onCreate) onCreate();
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao criar o lançamento.'));
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          mb: 4,
        }}
      >
        <Typography variant="h4" gutterBottom align="center">
          Nova Receita
        </Typography>
        {error &&
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        }
        {success &&
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        }
        <form onSubmit={handleSubmit}>
          <TextField
            label="Descrição"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.Name}
            value={selectedCategory}
            onChange={(event, newValue) => {
              setSelectedCategory(newValue);
              if (!newValue?.AllowsPositionLink) {
                setSelectedPosition(null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Categoria"
                variant="outlined"
                margin="normal"
                fullWidth
                required
              />
            )}
          />
          <TextField
            select
            label="Moeda"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            {Currencytypes.map((typeOption) => (
              <MenuItem key={typeOption.value} value={typeOption.value}>
                {typeOption.label}
              </MenuItem>
            ))}
          </TextField>
          <CurrencyInput
            label="Valor"
            variant="outlined"
            margin="normal"
            fullWidth
            required
            value={amount}
            onChange={setAmount}
            currency={currency}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
            <DatePicker
              label="Data de Recebimento"
              value={dayjs(receivedDate, 'DD/MM/YYYY')}
              onChange={(newValue) => setReceivedDate(newValue.format('DD/MM/YYYY'))}
              format="DD/MM/YYYY"
              slots={{
                textField: (params) => <TextField {...params} fullWidth margin="normal" />
              }}
            />
          </LocalizationProvider>
          {showPositionSelector && (
            <PositionSelector
              value={selectedPosition}
              onChange={setSelectedPosition}
              onError={setError}
            />
          )}
          <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
            <Grid item>
              <Button type="submit" variant="contained" color="primary" size="large">
                Cadastrar
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                size="large"
                onClick={() => {
                  setDescription('');
                  setSelectedCategory(null);
                  setCurrency('BRL');
                  setAmount(0);
                  setReceivedDate(dayjs().locale('pt-br').format('DD/MM/YYYY'));
                  setSelectedPosition(null);
                }}
              >
                Limpar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Container>
  );
};

export default CashInForm;